/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import { supabase } from './supabase';
import { KpiCard, DateStepper, ChartCard, StatusPill, SegmentedPills, PrimaryBtn, SecondaryBtn, DestructiveBtn, rangeForPeriod, priorPeriodRange } from './components/systemKit';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const DAY_OF_WEEK_LABELS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const CATEGORIES = ['Proteins', 'Produce', 'Dairy', 'Dry Goods', 'Beverages', 'Other', 'General/Mixed'];

// Returns 7 consecutive dates starting on the configured week-start day (0=Sun, 6=Sat).
// Used by Trends tab's weekly bucketing only — the main "This Period" view is
// period-aware via systemKit's rangeForPeriod / DateStepper.
function getWeekDates(offset = 0, weekStartDay = 0) {
  const now = new Date();
  const today = now.getDay();
  const backToStart = (today - weekStartDay + 7) % 7;
  const start = new Date(now);
  start.setDate(now.getDate() - backToStart + offset * 7);
  start.setHours(0, 0, 0, 0);
  return Array.from({ length: 7 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
}
function fmtDate(d) { return d.toISOString().split('T')[0]; }
function fmt$(n) { return `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`; }
function fmtPct(n) { return n != null && isFinite(n) ? `${n.toFixed(1)}%` : '\u2014'; }
// Tone derivation for %-vs-target KPIs — see DESIGN_SYSTEM.md §Cards (meaning-driven variant).
function pctTone(val, target) {
  if (val == null) return 'neutral';
  if (val <= target - 3) return 'success';
  if (val <= target + 3) return 'warning';
  return 'critical';
}

export default function FoodCost({ companyId }) {
  const [tab, setTab] = useState('period');
  // Period-aware date control. Single source of truth across all data pages —
  // see DESIGN_SYSTEM.md §Period-aware DateStepper. Replaces the previous
  // Week|Range pill toggle + custom date inputs + 7d/30d/90d/MTD/YTD shortcuts.
  const [period, setPeriod] = useState('week');
  const [range, setRange] = useState(() => rangeForPeriod('week', new Date()));
  const [invoices, setInvoices] = useState([]);
  const [entries, setEntries] = useState({});
  const [sales, setSales] = useState({});
  // Previous-period aggregates (for "vs last week" / "vs previous period" card in range mode)
  const [prevEntriesTotal, setPrevEntriesTotal] = useState(0);
  const [vendors, setVendors] = useState([]);
  const [settings, setSettings] = useState({ target_food_cost_pct: 30, target_prime_cost_pct: 60, week_start_day: 0 });
  const [cleanupStatus, setCleanupStatus] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ amount: '', vendor_id: '', date: fmtDate(new Date()), invoice_number: '', notes: '', showDetails: false });
  const [addingVendorInline, setAddingVendorInline] = useState(false);
  const [inlineVendorName, setInlineVendorName] = useState('');
  const [saved, setSaved] = useState('');
  const [trendRange, setTrendRange] = useState(8);
  const [trendData, setTrendData] = useState([]);
  const [vendorExpanded, setVendorExpanded] = useState(null);
  const [vendorInvoices, setVendorInvoices] = useState([]);
  const [showAddVendor, setShowAddVendor] = useState(false);
  const [vendorForm, setVendorForm] = useState({ name: '', category: '', delivery_days: '', phone: '', email: '', notes: '' });
  const [vendorDeliveryDays, setVendorDeliveryDays] = useState([]);

  const weekStartDay = Number(settings.week_start_day) || 0;
  const target = settings.target_food_cost_pct || 30;

  // datesInRange — string array of every YYYY-MM-DD in the [range.start, range.end]
  // window. Used for in-memory aggregation; queries themselves use .gte/.lte.
  function datesInRange(startIso, endIso) {
    const out = [];
    const s = new Date(startIso + 'T00:00:00');
    const e = new Date(endIso + 'T00:00:00');
    for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) out.push(fmtDate(d));
    return out;
  }
  const activeStart = range.start;
  const activeEnd = range.end;
  const activeDates = datesInRange(activeStart, activeEnd);

  useEffect(() => {
    if (companyId) fetchWindowData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, range.start, range.end]);
  useEffect(() => { if (companyId && tab === 'trends') fetchTrends(); }, [companyId, tab, trendRange, weekStartDay]);
  useEffect(() => { if (companyId) fetchVendorsAndSettings(); }, [companyId]);

  async function fetchWindowData() {
    // Current window
    const [invRes, entryRes, salesRes] = await Promise.all([
      supabase.from('food_invoices').select('*').eq('company_id', companyId)
        .gte('date', activeStart).lte('date', activeEnd).order('date', { ascending: false }),
      supabase.from('food_cost_entries').select('*').eq('company_id', companyId)
        .gte('date', activeStart).lte('date', activeEnd),
      supabase.from('labor_sales').select('*').eq('company_id', companyId)
        .gte('date', activeStart).lte('date', activeEnd),
    ]);
    setInvoices(invRes.data || []);
    const em = {}; (entryRes.data || []).forEach(e => { em[e.date] = e; }); setEntries(em);
    const sm = {}; (salesRes.data || []).forEach(s => { sm[s.date] = s; }); setSales(sm);

    // Prior-period total for the "vs previous" card. Calendar-aligned via
    // priorPeriodRange — week→prior week, month→prior month, etc. — so the
    // comparison stays semantically meaningful across all period selections.
    const prior = priorPeriodRange(period, range);
    const { data: prevEntries } = await supabase.from('food_cost_entries')
      .select('total')
      .eq('company_id', companyId)
      .gte('date', prior.start)
      .lte('date', prior.end);
    setPrevEntriesTotal((prevEntries || []).reduce((s, r) => s + Number(r.total || 0), 0));
  }

  async function fetchVendorsAndSettings() {
    const [vRes, sRes] = await Promise.all([
      supabase.from('food_vendors').select('*').eq('company_id', companyId).order('name'),
      supabase.from('food_cost_settings').select('*').eq('company_id', companyId).single(),
    ]);
    setVendors(vRes.data || []);
    if (sRes.data) setSettings(sRes.data);
  }

  async function fetchTrends() {
    const weeks = [];
    for (let i = 0; i < trendRange; i++) {
      const wd = getWeekDates(-i, weekStartDay);
      weeks.push({ start: fmtDate(wd[0]), end: fmtDate(wd[6]), label: `${wd[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` });
    }
    const start = weeks[weeks.length - 1].start;
    const end = weeks[0].end;
    const [eRes, sRes] = await Promise.all([
      supabase.from('food_cost_entries').select('date, total').eq('company_id', companyId).gte('date', start).lte('date', end),
      supabase.from('labor_sales').select('date, total_sales').eq('company_id', companyId).gte('date', start).lte('date', end),
    ]);
    const allEntries = eRes.data || [];
    const allSales = sRes.data || [];
    const result = weeks.map(w => {
      const foodTotal = allEntries.filter(e => e.date >= w.start && e.date <= w.end).reduce((s, e) => s + (e.total || 0), 0);
      const salesTotal = allSales.filter(s => s.date >= w.start && s.date <= w.end).reduce((s, e) => s + (e.total_sales || 0), 0);
      return { ...w, food: foodTotal, sales: salesTotal, pct: salesTotal > 0 ? (foodTotal / salesTotal) * 100 : null };
    }).reverse();
    setTrendData(result);
  }

  // ── Add Cost ──
  async function saveAddCost() {
    const amount = parseFloat(addForm.amount);
    if (!amount || amount <= 0) return;
    const date = addForm.date || fmtDate(new Date());

    if (addForm.vendor_id) {
      await supabase.from('food_invoices').insert([{ company_id: companyId, vendor_id: parseInt(addForm.vendor_id), date, total: amount, invoice_number: addForm.invoice_number || null, notes: addForm.notes || null, source: 'manual' }]);
    }
    // Upsert food_cost_entries
    const existing = entries[date];
    const newTotal = (existing?.total || 0) + amount;
    if (existing?.id) {
      await supabase.from('food_cost_entries').update({ total: newTotal, updated_at: new Date().toISOString() }).eq('id', existing.id);
    } else {
      await supabase.from('food_cost_entries').insert([{ company_id: companyId, date, total: newTotal, source: 'manual' }]);
    }
    setShowAdd(false);
    setAddForm({ amount: '', vendor_id: '', date: fmtDate(new Date()), invoice_number: '', notes: '', showDetails: false });
    flash('Cost added');
    fetchWindowData();
  }

  async function addInlineVendor() {
    if (!inlineVendorName.trim()) return;
    const { data } = await supabase.from('food_vendors').insert([{ company_id: companyId, name: inlineVendorName.trim() }]).select();
    if (data?.[0]) { setAddForm(p => ({ ...p, vendor_id: String(data[0].id) })); await fetchVendorsAndSettings(); }
    setAddingVendorInline(false); setInlineVendorName('');
  }

  async function deleteInvoice(inv) {
    if (!window.confirm(`Delete ${fmt$(inv.total)} invoice?`)) return;
    await supabase.from('food_invoices').delete().eq('id', inv.id);
    // Recalculate entry total for that date
    const { data: remaining } = await supabase.from('food_invoices').select('total').eq('company_id', companyId).eq('date', inv.date);
    const newTotal = (remaining || []).reduce((s, r) => s + (r.total || 0), 0);
    const entry = entries[inv.date];
    if (entry?.id) {
      if (newTotal > 0) await supabase.from('food_cost_entries').update({ total: newTotal }).eq('id', entry.id);
      else await supabase.from('food_cost_entries').delete().eq('id', entry.id);
    }
    flash('Deleted'); fetchWindowData();
  }

  // ── Vendor CRUD ──
  async function saveVendor() {
    if (!vendorForm.name) return;
    await supabase.from('food_vendors').insert([{ company_id: companyId, ...vendorForm, delivery_days: vendorDeliveryDays.join(',') }]);
    setShowAddVendor(false); setVendorForm({ name: '', category: '', delivery_days: '', phone: '', email: '', notes: '' }); setVendorDeliveryDays([]);
    fetchVendorsAndSettings();
  }
  async function deleteVendor(id) { if (window.confirm('Remove this vendor?')) { await supabase.from('food_vendors').delete().eq('id', id); fetchVendorsAndSettings(); } }
  async function expandVendor(id) {
    if (vendorExpanded === id) { setVendorExpanded(null); return; }
    setVendorExpanded(id);
    const { data } = await supabase.from('food_invoices').select('*').eq('company_id', companyId).eq('vendor_id', id).order('date', { ascending: false }).limit(20);
    setVendorInvoices(data || []);
  }

  // ── Settings ──
  async function saveSettings() {
    await supabase.from('food_cost_settings').upsert([{
      company_id: companyId,
      target_food_cost_pct: settings.target_food_cost_pct,
      target_prime_cost_pct: settings.target_prime_cost_pct,
      week_start_day: Number(settings.week_start_day) || 0,
    }], { onConflict: 'company_id' });
    flash('Settings saved');
  }

  function flash(msg) { setSaved(msg); setTimeout(() => setSaved(''), 2000); }

  // Remove food_invoices rows that were synced from Price Tracker but no longer match
  // a current Price Tracker invoice (orphans from deletes, duplicates from re-imports).
  // Mirrors the cleanup in FoodPriceTracker → Settings so users can trigger it from whichever
  // tab they're looking at.
  async function cleanupSyncedInvoices() {
    if (!companyId) return;
    if (!window.confirm('Remove synced Food Cost rows that no longer match a Price Tracker invoice? (Manual Food Cost entries are not affected.)')) return;
    setCleanupStatus('running');
    try {
      const { data: sourceInvs } = await supabase
        .from('invoices')
        .select('invoice_date, vendor_name, total_amount')
        .eq('company_id', companyId);
      const validCounts = new Map();
      (sourceInvs || []).forEach(r => {
        const sig = `${r.invoice_date}|${(r.vendor_name || '').toLowerCase()}|${Math.round(Number(r.total_amount || 0) * 100)}`;
        validCounts.set(sig, (validCounts.get(sig) || 0) + 1);
      });

      const { data: synced } = await supabase
        .from('food_invoices')
        .select('id, date, total, vendor_id, created_at, food_vendors(name)')
        .eq('company_id', companyId)
        .eq('source', 'price_tracker')
        .order('created_at', { ascending: true });

      const remainingCounts = new Map(validCounts);
      const toDelete = [];
      const affectedDates = new Set();
      (synced || []).forEach(r => {
        const vendorName = (r.food_vendors?.name || '').toLowerCase();
        const sig = `${r.date}|${vendorName}|${Math.round(Number(r.total || 0) * 100)}`;
        const remaining = remainingCounts.get(sig) || 0;
        if (remaining > 0) {
          remainingCounts.set(sig, remaining - 1);
        } else {
          toDelete.push(r.id);
          affectedDates.add(r.date);
        }
      });

      if (toDelete.length > 0) {
        await supabase.from('food_invoices').delete().in('id', toDelete);
      }

      for (const d of affectedDates) {
        const { data: remainingOnDate } = await supabase
          .from('food_invoices')
          .select('total')
          .eq('company_id', companyId)
          .eq('date', d);
        const newTotal = (remainingOnDate || []).reduce((s, r) => s + Number(r.total || 0), 0);
        const { data: entry } = await supabase
          .from('food_cost_entries')
          .select('id')
          .eq('company_id', companyId)
          .eq('date', d)
          .maybeSingle();
        if (entry) {
          if (newTotal > 0) {
            await supabase.from('food_cost_entries').update({ total: newTotal, updated_at: new Date().toISOString() }).eq('id', entry.id);
          } else {
            await supabase.from('food_cost_entries').delete().eq('id', entry.id);
          }
        }
      }

      setCleanupStatus(`Removed ${toDelete.length} orphaned / duplicate row(s).`);
      await fetchWindowData();
    } catch (e) {
      setCleanupStatus(`Cleanup failed: ${e.message}`);
    }
  }

  // ── Period calculations ──
  // Window-aware totals — entries are keyed by date string and we only
  // aggregate dates inside the active window.
  const weekFoodTotal = activeDates.reduce((s, d) => s + (entries[d]?.total || 0), 0);
  const weekSalesTotal = activeDates.reduce((s, d) => s + (sales[d]?.total_sales || 0), 0);
  const weekFoodPct = weekSalesTotal > 0 ? (weekFoodTotal / weekSalesTotal) * 100 : null;
  const hasSales = weekSalesTotal > 0;

  // Previous period comparison — calendar-aligned by period.
  const prevWeekTotal = prevEntriesTotal;
  const weekDelta = weekFoodTotal - prevWeekTotal;
  const prevLabel = period === 'week'    ? 'vs Last Week'
                  : period === 'month'   ? 'vs Last Month'
                  : period === 'quarter' ? 'vs Last Quarter'
                  :                        'vs Last Year';
  const periodNoun = period === 'week'    ? 'week'
                   : period === 'month'   ? 'month'
                   : period === 'quarter' ? 'quarter'
                   :                        'year';

  // Last invoice for selected vendor
  const lastInvoiceForVendor = addForm.vendor_id ? invoices.concat().sort((a, b) => b.date?.localeCompare(a.date)).find(inv => String(inv.vendor_id) === addForm.vendor_id) : null;

  const tabs = [
    { id: 'period', label: 'This Period' },
    { id: 'trends', label: 'Trends' },
    { id: 'vendors', label: 'Vendors' },
    { id: 'settings', label: 'Settings' },
  ];

  return (
    <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900" style={{ letterSpacing: '-0.01em' }}>Food Costs</h1>
            <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>Track invoices and spending</p>
          </div>
          <div className="flex items-center gap-2">
            {saved && <StatusPill tone="green">{'\u2713'} {saved}</StatusPill>}
            <PrimaryBtn onClick={() => { setShowAdd(true); setAddForm(p => ({ ...p, amount: '', date: fmtDate(new Date()) })); }}>
              + Add Cost
            </PrimaryBtn>
          </div>
        </div>

        {/* Tabs — view switching on the same dataset → segmented pills */}
        <div className="mb-6">
          <SegmentedPills
            value={tab}
            onChange={setTab}
            options={tabs.map(t => ({ key: t.id, label: t.label }))}
          />
        </div>

        {/* ═══ THIS PERIOD TAB ═══ */}
        {tab === 'period' && (
          <div>
            {/* Period-aware DateStepper — canonical date control across all data
                pages. See DESIGN_SYSTEM.md §Period-aware DateStepper. */}
            <div className="flex items-center justify-end gap-3 mb-6 flex-wrap">
              <DateStepper
                period={period}
                onPeriodChange={setPeriod}
                range={range}
                onRangeChange={setRange}
                alignment="right"
              />
            </div>

            {/* Summary cards — KpiCard's meaning-driven variant applies a 3px left accent bar
                + soft functional tint for at-a-glance state, per DESIGN_SYSTEM.md §Cards. */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <KpiCard
                label="Food Cost"
                value={weekFoodTotal > 0 ? fmt$(weekFoodTotal) : '\u2014'}
                meaning={weekFoodPct != null ? pctTone(weekFoodPct, target) : undefined}
                secondary={weekFoodPct != null ? `${fmtPct(weekFoodPct)} of sales` : hasSales ? '\u2014' : 'Add sales to see %'}
              />
              <KpiCard
                label="Prime Cost"
                value={hasSales ? fmtPct(weekFoodPct != null ? weekFoodPct + 25 : null) : '\u2014'}
                secondary={hasSales ? 'food + labor' : 'Need sales data'}
              />
              <KpiCard
                label={prevLabel}
                value={prevWeekTotal > 0
                  ? `${weekDelta <= 0 ? '\u2193' : '\u2191'} ${fmt$(Math.abs(weekDelta))}`
                  : '\u2014'}
                valueTone={prevWeekTotal > 0 ? (weekDelta <= 0 ? 'success' : 'critical') : 'neutral'}
                secondary={prevWeekTotal > 0
                  ? `${((weekDelta / prevWeekTotal) * 100).toFixed(1)}% change`
                  : `No data last ${periodNoun}`}
              />
            </div>

            {/* Invoice log */}
            {invoices.length === 0 && weekFoodTotal === 0 ? (
              <div className="rounded-lg p-12 text-center" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
                <div className="text-3xl mb-3">{'\uD83D\uDCE6'}</div>
                <div className="text-sm font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>{`No food costs in this ${periodNoun}`}</div>
                <div className="text-xs mb-4" style={{ color: 'var(--text-muted)' }}>Tap the button to log your first delivery invoice</div>
                <div className="inline-flex">
                  <PrimaryBtn onClick={() => setShowAdd(true)}>+ Add Cost</PrimaryBtn>
                </div>
              </div>
            ) : (
              <div className="rounded-lg overflow-hidden" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
                <div className="px-4 py-3 flex items-center justify-between" style={{ background: 'var(--bg-subtle)', borderBottom: '1px solid var(--border)' }}>
                  <div style={{ fontSize: 12, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)' }}>{`Invoices this ${periodNoun}`}</div>
                  <StatusPill tone="neutral">{invoices.length} entr{invoices.length === 1 ? 'y' : 'ies'}</StatusPill>
                </div>
                {invoices.map((inv, i) => {
                  const vendor = vendors.find(v => v.id === inv.vendor_id);
                  return (
                    <div
                      key={inv.id}
                      className="flex items-center justify-between px-4 transition-colors"
                      style={{
                        minHeight: 52,
                        paddingTop: 10,
                        paddingBottom: 10,
                        borderBottom: i < invoices.length - 1 ? '1px solid var(--border)' : 'none',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-subtle)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{vendor?.name || 'Quick entry'}</span>
                          {inv.invoice_number && <span className="text-xs" style={{ color: 'var(--text-muted)' }}>#{inv.invoice_number}</span>}
                        </div>
                        <div className="text-xs mt-0.5" style={{ color: 'var(--text-secondary)' }}>
                          {new Date(inv.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                          {inv.notes && ` \u00B7 "${inv.notes}"`}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>{fmt$(inv.total)}</span>
                        <button onClick={() => deleteInvoice(inv)} className="text-xs" style={{ color: 'var(--text-critical)', background: 'transparent', border: 'none', cursor: 'pointer' }}>{'\u2715'}</button>
                      </div>
                    </div>
                  );
                })}
                {/* Quick entries without invoices */}
                {activeDates.map(ds => {
                  const entry = entries[ds];
                  const dayInvoiceTotal = invoices.filter(inv => inv.date === ds).reduce((s, inv) => s + (inv.total || 0), 0);
                  const remainder = (entry?.total || 0) - dayInvoiceTotal;
                  if (remainder <= 0.01) return null;
                  return (
                    <div key={ds} className="flex items-center justify-between px-4 py-3" style={{ borderTop: '1px solid var(--border)', minHeight: 52 }}>
                      <div>
                        <span className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Quick entry</span>
                        <div className="text-xs" style={{ color: 'var(--text-muted)' }}>{new Date(ds + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</div>
                      </div>
                      <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>{fmt$(remainder)}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ═══ TRENDS TAB ═══ */}
        {tab === 'trends' && (
          <div>
            {/* Range selector */}
            <div className="mb-6">
              <SegmentedPills
                value={trendRange}
                onChange={setTrendRange}
                options={[
                  { key: 8,  label: '8 weeks' },
                  { key: 12, label: '12 weeks' },
                  { key: 26, label: '6 months' },
                  { key: 52, label: '12 months' },
                ]}
              />
            </div>

            {trendData.filter(w => w.food > 0).length < 2 ? (
              <div className="rounded-lg p-12 text-center" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
                <div className="text-3xl mb-3">{'\uD83D\uDCC8'}</div>
                <div className="text-sm font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>Keep logging your food costs</div>
                <div className="text-xs" style={{ color: 'var(--text-muted)' }}>Trends will appear here once you have a few weeks of data</div>
              </div>
            ) : (
              <>
                {/* Line chart — responsive SVG. Always fills width, no horizontal scroll needed.
                    Month tick labels below the axis are readable at any range (8w, 12w, 6mo, 12mo). */}
                <div className="mb-6">
                <ChartCard title="Weekly Food Cost">
                  {/* (SVG rendered inline below keeps the existing math verbatim.) */}
                  {(() => {
                    const width = 900, height = 200, padL = 44, padR = 12, padT = 14, padB = 28;
                    const chartW = width - padL - padR;
                    const chartH = height - padT - padB;
                    const maxFood = Math.max(1, ...trendData.map(t => t.food));
                    const n = trendData.length;
                    const x = (i) => padL + (n === 1 ? chartW / 2 : (i / (n - 1)) * chartW);
                    const y = (v) => padT + chartH - (v / maxFood) * chartH;

                    // Month ticks: show the start of each month in the range.
                    const monthTicks = [];
                    let lastMonth = '';
                    trendData.forEach((w, i) => {
                      const d = new Date(w.start + 'T00:00:00');
                      const key = `${d.getFullYear()}-${d.getMonth()}`;
                      if (key !== lastMonth) {
                        monthTicks.push({ i, label: d.toLocaleDateString('en-US', { month: 'short' }) + (n > 30 ? ` '${String(d.getFullYear()).slice(2)}` : '') });
                        lastMonth = key;
                      }
                    });

                    // Y-axis: 4 gridlines at 0, 25%, 50%, 75%, 100% of max.
                    const yTicks = [0, 0.25, 0.5, 0.75, 1].map(p => ({ v: maxFood * p, y: y(maxFood * p) }));

                    // Build the polyline path
                    const pts = trendData.map((w, i) => ({ x: x(i), y: y(w.food), w, i }));
                    const linePath = pts.map(p => `${p.x},${p.y}`).join(' ');
                    // Fill area from bottom of chart up to the line
                    const areaPath = pts.length > 0
                      ? `${pts[0].x},${padT + chartH} ${linePath} ${pts[pts.length - 1].x},${padT + chartH}`
                      : '';

                    // Target pct line — only drawn if we have any sales data (pct values exist)
                    const hasPct = trendData.some(w => w.pct != null);

                    return (
                      <svg viewBox={`0 0 ${width} ${height}`} className="w-full" style={{ maxHeight: 260 }} preserveAspectRatio="xMidYMid meet">
                        {/* Gridlines + y labels — chrome, use CSS vars. */}
                        {yTicks.map((t, i) => (
                          <g key={i}>
                            <line x1={padL} y1={t.y} x2={width - padR} y2={t.y} stroke="var(--border)" strokeWidth="1" />
                            <text x={padL - 6} y={t.y + 3} textAnchor="end" fontSize="10" fill="var(--text-muted)">
                              ${t.v >= 1000 ? `${(t.v / 1000).toFixed(t.v >= 10000 ? 0 : 1)}k` : Math.round(t.v)}
                            </text>
                          </g>
                        ))}

                        {/* Month tick labels on x-axis — chrome. */}
                        {monthTicks.map((t, i) => (
                          <g key={i}>
                            <line x1={x(t.i)} y1={padT + chartH} x2={x(t.i)} y2={padT + chartH + 4} stroke="var(--border)" strokeWidth="1" />
                            <text x={x(t.i)} y={padT + chartH + 18} textAnchor="middle" fontSize="11" fill="var(--text-secondary)">{t.label}</text>
                          </g>
                        ))}

                        {/* Data series — line color + area fill stay hardcoded per §Data Colors. */}
                        {pts.length >= 2 && (
                          <polygon points={areaPath} fill="rgba(37, 99, 235, 0.08)" />
                        )}
                        {pts.length >= 2 && (
                          <polyline fill="none" stroke="#2563EB" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" points={linePath} />
                        )}

                        {/* Dots — data color (good/near/over threshold) per Data Colors rule. */}
                        {pts.map((p) => {
                          const w = p.w;
                          const color = w.pct != null
                            ? (w.pct <= target - 3 ? '#22C55E' : w.pct <= target + 3 ? '#F59E0B' : '#EF4444')
                            : '#94A3B8';
                          const isLast = p.i === n - 1;
                          return (
                            <g key={p.i}>
                              <title>{`${w.label}: ${fmt$(w.food)}${w.pct != null ? ` (${fmtPct(w.pct)})` : ''}`}</title>
                              <circle cx={p.x} cy={p.y} r={isLast ? 5 : 3.5} fill={color} stroke="var(--bg-card)" strokeWidth="2" />
                            </g>
                          );
                        })}

                        {/* Last-point value label — chrome text. */}
                        {pts.length > 0 && (() => {
                          const last = pts[pts.length - 1];
                          const w = last.w;
                          if (!w.food) return null;
                          const labelX = Math.min(last.x, width - padR - 4);
                          return (
                            <g>
                              <text x={labelX} y={last.y - 10} textAnchor="end" fontSize="11" fontWeight="600" fill="var(--text-primary)">
                                ${w.food >= 1000 ? `${(w.food / 1000).toFixed(w.food >= 10000 ? 0 : 1)}k` : Math.round(w.food)}
                              </text>
                            </g>
                          );
                        })()}

                        {!hasPct && (
                          <text x={padL} y={padT + 12} fontSize="10" fill="var(--text-muted)">Add daily sales to color dots by food-cost %</text>
                        )}
                      </svg>
                    );
                  })()}
                  {/* Target line label */}
                  <div className="flex items-center gap-2 mt-3 text-xs" style={{ color: 'var(--text-muted)' }}>
                    <div className="w-4" style={{ borderTop: '2px dashed var(--border)' }} />
                    Target: {target}%
                    <div className="flex items-center gap-3 ml-auto">
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm" style={{ background: '#22C55E' }} /> Under</span>
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm" style={{ background: '#F59E0B' }} /> Near</span>
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm" style={{ background: '#EF4444' }} /> Over</span>
                    </div>
                  </div>
                </ChartCard>
                </div>

                {/* Week-by-week table */}
                <div className="rounded-lg overflow-hidden" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
                  <div className="grid" style={{ gridTemplateColumns: '1fr 1fr 1fr 80px', background: 'var(--bg-subtle)', borderBottom: '1px solid var(--border)' }}>
                    <div className="px-3 py-2" style={{ fontSize: 12, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)' }}>Week</div>
                    <div className="px-3 py-2" style={{ fontSize: 12, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)', textAlign: 'right' }}>Food Cost</div>
                    <div className="px-3 py-2" style={{ fontSize: 12, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)', textAlign: 'right' }}>Sales</div>
                    <div className="px-3 py-2" style={{ fontSize: 12, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)', textAlign: 'right' }}>%</div>
                  </div>
                  {trendData.filter(w => w.food > 0).reverse().map((w, i, arr) => {
                    const best = arr.length > 2 && w.pct != null && w.pct === Math.min(...arr.filter(x => x.pct != null).map(x => x.pct));
                    const worst = arr.length > 2 && w.pct != null && w.pct === Math.max(...arr.filter(x => x.pct != null).map(x => x.pct));
                    const tone = w.pct != null ? pctTone(w.pct, target) : 'neutral';
                    const toneColor = ({ success: 'var(--text-success)', warning: 'var(--text-warning)', critical: 'var(--text-critical)', neutral: 'var(--text-muted)' })[tone];
                    return (
                      <div
                        key={i}
                        className="grid"
                        style={{
                          gridTemplateColumns: '1fr 1fr 1fr 80px',
                          borderBottom: i < arr.length - 1 ? '1px solid var(--border)' : 'none',
                          background: best ? 'var(--bg-success-subtle)' : worst ? 'var(--bg-critical-subtle)' : 'transparent',
                          minHeight: 44,
                        }}
                      >
                        <div className="px-3 py-2.5 text-xs" style={{ color: 'var(--text-secondary)' }}>{w.label}</div>
                        <div className="px-3 py-2.5 text-xs font-semibold" style={{ color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>{fmt$(w.food)}</div>
                        <div className="px-3 py-2.5 text-xs" style={{ color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>{w.sales > 0 ? fmt$(w.sales) : '\u2014'}</div>
                        <div className="px-3 py-2.5 text-xs font-semibold" style={{ color: toneColor, fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>{w.pct != null ? fmtPct(w.pct) : '\u2014'}</div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}

        {/* ═══ VENDORS TAB ═══ */}
        {tab === 'vendors' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <div className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{vendors.length} vendor{vendors.length !== 1 ? 's' : ''}</div>
              <PrimaryBtn onClick={() => setShowAddVendor(true)} className="!px-3 !py-1.5 !text-xs">+ Add vendor</PrimaryBtn>
            </div>
            <div className="rounded-lg overflow-hidden" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
              {vendors.length === 0 && <div className="px-6 py-12 text-center text-sm" style={{ color: 'var(--text-muted)' }}>No vendors yet. Add your first food vendor.</div>}
              {vendors.map((v, i) => (
                <div key={v.id} style={{ borderBottom: i < vendors.length - 1 ? '1px solid var(--border)' : 'none' }}>
                  <div
                    className="flex items-center justify-between px-4 py-3 cursor-pointer transition-colors"
                    style={{ minHeight: 52 }}
                    onClick={() => expandVendor(v.id)}
                    onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-subtle)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{v.name}</span>
                        {v.category && <StatusPill tone="neutral">{v.category}</StatusPill>}
                      </div>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        {v.delivery_days && v.delivery_days.split(',').map(d => d.trim()).filter(Boolean).map(d => (
                          <span key={d} className="px-1.5 py-0.5 rounded-md font-semibold" style={{ fontSize: 9, background: 'var(--bg-info-subtle)', color: 'var(--text-info)' }}>{d}</span>
                        ))}
                        {v.phone && <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{v.phone}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{vendorExpanded === v.id ? '\u25B2' : '\u25BC'}</span>
                      <button onClick={(e) => { e.stopPropagation(); deleteVendor(v.id); }} className="text-xs" style={{ color: 'var(--text-critical)', background: 'transparent', border: 'none', cursor: 'pointer' }}>Remove</button>
                    </div>
                  </div>
                  {vendorExpanded === v.id && (
                    <div className="px-4 pb-3" style={{ borderTop: '1px solid var(--border)', background: 'var(--bg-subtle)' }}>
                      {vendorInvoices.length === 0 ? (
                        <div className="text-xs py-3 text-center" style={{ color: 'var(--text-muted)' }}>No invoices for this vendor yet</div>
                      ) : vendorInvoices.map(inv => (
                        <div key={inv.id} className="flex items-center justify-between py-2 text-xs">
                          <span style={{ color: 'var(--text-secondary)' }}>{new Date(inv.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                          <span className="font-semibold" style={{ color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>{fmt$(inv.total)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
            {showAddVendor && (
              <div className="rounded-lg p-5 mt-4" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
                <div className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Add Vendor</div>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  {[
                    { key: 'name',     label: 'Name *',  placeholder: 'e.g. Sysco' },
                    { key: 'phone',    label: 'Phone',   placeholder: '(555) 000-0000' },
                    { key: 'email',    label: 'Email',   placeholder: 'orders@vendor.com' },
                  ].map(f => (
                    <div key={f.key}>
                      <label className="block mb-1" style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)' }}>{f.label}</label>
                      <input
                        value={vendorForm[f.key]} onChange={e => setVendorForm(p => ({ ...p, [f.key]: e.target.value }))}
                        placeholder={f.placeholder}
                        className="w-full rounded-lg px-3 py-2 text-sm focus:outline-none"
                        style={{ border: '1px solid var(--border)', background: 'var(--bg-subtle)', color: 'var(--text-primary)' }}
                        onFocus={e => { e.currentTarget.style.borderColor = 'var(--accent-primary)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(109, 40, 217, 0.1)'; }}
                        onBlur={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; }}
                      />
                    </div>
                  ))}
                  <div>
                    <label className="block mb-1" style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)' }}>Category</label>
                    <select
                      value={vendorForm.category} onChange={e => setVendorForm(p => ({ ...p, category: e.target.value }))}
                      className="w-full rounded-lg px-3 py-2 text-sm focus:outline-none"
                      style={{ border: '1px solid var(--border)', background: 'var(--bg-subtle)', color: 'var(--text-primary)' }}
                    >
                      <option value="">Select</option>
                      {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                    </select>
                  </div>
                </div>
                <div className="mb-3">
                  <label className="block mb-1" style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)' }}>Delivery days</label>
                  <div className="grid grid-cols-7 gap-1">
                    {DAYS.map(d => {
                      const on = vendorDeliveryDays.includes(d);
                      return (
                        <button
                          key={d}
                          onClick={() => setVendorDeliveryDays(p => p.includes(d) ? p.filter(x => x !== d) : [...p, d])}
                          className="py-1.5 rounded-md text-xs font-medium"
                          style={{
                            background: on ? 'var(--accent-primary)' : 'var(--bg-subtle)',
                            color: on ? '#FFFFFF' : 'var(--text-secondary)',
                            border: '1px solid ' + (on ? 'var(--accent-primary)' : 'var(--border)'),
                            cursor: 'pointer',
                          }}
                        >
                          {d}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="flex gap-2">
                  <SecondaryBtn onClick={() => setShowAddVendor(false)} className="flex-1 !py-2">Cancel</SecondaryBtn>
                  <PrimaryBtn onClick={saveVendor} className="flex-1 !py-2">Save</PrimaryBtn>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ═══ SETTINGS TAB ═══ */}
        {tab === 'settings' && (
          <div className="space-y-4">
            <div className="rounded-lg p-5" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
              <div className="text-sm font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>Cost Targets</div>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block mb-1.5" style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)' }}>Target Food Cost %</label>
                  <div className="flex items-center gap-3">
                    <input type="range" min="15" max="45" value={settings.target_food_cost_pct || 30} onChange={e => setSettings(p => ({ ...p, target_food_cost_pct: parseInt(e.target.value) }))} className="flex-1" />
                    <span className="text-sm font-semibold w-10" style={{ color: 'var(--text-primary)' }}>{settings.target_food_cost_pct || 30}%</span>
                  </div>
                  <div className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>Most restaurants target 28-32%</div>
                </div>
                <div>
                  <label className="block mb-1.5" style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)' }}>Target Prime Cost %</label>
                  <div className="flex items-center gap-3">
                    <input type="range" min="45" max="70" value={settings.target_prime_cost_pct || 60} onChange={e => setSettings(p => ({ ...p, target_prime_cost_pct: parseInt(e.target.value) }))} className="flex-1" />
                    <span className="text-sm font-semibold w-10" style={{ color: 'var(--text-primary)' }}>{settings.target_prime_cost_pct || 60}%</span>
                  </div>
                  <div className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>Food + labor combined, target 55-65%</div>
                </div>
              </div>
              <div className="mb-4 pt-4" style={{ borderTop: '1px solid var(--border)' }}>
                <label className="block mb-1.5" style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)' }}>Week starts on</label>
                <select
                  value={weekStartDay}
                  onChange={e => setSettings(p => ({ ...p, week_start_day: parseInt(e.target.value, 10) }))}
                  className="rounded-lg px-3 py-2 text-sm focus:outline-none"
                  style={{ border: '1px solid var(--border)', background: 'var(--bg-subtle)', color: 'var(--text-primary)' }}
                  onFocus={e => { e.currentTarget.style.borderColor = 'var(--accent-primary)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(109, 40, 217, 0.1)'; }}
                  onBlur={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; }}
                >
                  {DAY_OF_WEEK_LABELS.map((label, i) => <option key={i} value={i}>{label}</option>)}
                </select>
                <div className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>Drives the week navigator and the Weekly Sales entry window.</div>
              </div>
              <PrimaryBtn onClick={saveSettings}>Save targets</PrimaryBtn>
            </div>

            {/* Price Tracker Sync */}
            <div className="rounded-lg p-5" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
              <div className="text-sm font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>Price Tracker Sync</div>
              <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="text-xs mb-1" style={{ color: 'var(--text-secondary)' }}>Sync is currently:</div>
                  <div className="flex items-center gap-2">
                    <StatusPill tone={settings.price_tracker_sync_enabled ? 'green' : 'neutral'}>
                      <span className="inline-flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full" style={{ background: settings.price_tracker_sync_enabled ? 'var(--text-success)' : 'var(--text-muted)' }} />
                        {settings.price_tracker_sync_enabled ? 'On' : 'Off'}
                      </span>
                    </StatusPill>
                  </div>
                  <div className="text-xs mt-2 leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                    When on, Price Tracker invoices automatically count toward your Food Cost totals.
                    When off, Food Cost only shows manual entries you add here.
                  </div>
                </div>
              </div>
              <div className="text-xs mb-4" style={{ color: 'var(--text-muted)' }}>Change this setting in Price Tracker {'\u2192'} Settings.</div>

              <div className="pt-4" style={{ borderTop: '1px solid var(--border)' }}>
                <div className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Clean up synced rows</div>
                <div className="text-xs mt-1 leading-relaxed mb-3" style={{ color: 'var(--text-secondary)' }}>
                  Removes Food Cost rows synced from Price Tracker that no longer match a current Price Tracker invoice
                  (orphans from deletes, duplicates from re-imports). Manual entries are not touched.
                </div>
                <div className="flex items-center gap-3">
                  <DestructiveBtn
                    onClick={cleanupSyncedInvoices}
                    disabled={cleanupStatus === 'running'}
                    className="!px-3 !py-1.5 !text-xs"
                  >
                    {cleanupStatus === 'running' ? 'Cleaning...' : 'Clean up synced invoices'}
                  </DestructiveBtn>
                  {cleanupStatus && cleanupStatus !== 'running' && (
                    <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>{cleanupStatus}</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Add Cost Modal ── */}
      {showAdd && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}>
          <div className="rounded-lg p-6 w-full max-w-sm mx-4" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
            <div className="flex items-center justify-between mb-5">
              <div className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>Add Cost</div>
              <button
                onClick={() => { setShowAdd(false); setAddingVendorInline(false); }}
                className="text-lg px-1 rounded-md transition-colors"
                style={{ color: 'var(--text-secondary)', background: 'transparent', border: 'none', cursor: 'pointer' }}
                onMouseEnter={e => { e.currentTarget.style.color = 'var(--text-primary)'; e.currentTarget.style.background = 'var(--bg-hover)'; }}
                onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-secondary)'; e.currentTarget.style.background = 'transparent'; }}
              >{'\u2715'}</button>
            </div>

            {/* Amount */}
            <div className="mb-4">
              <label className="block mb-1.5" style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)' }}>Amount</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-xl" style={{ color: 'var(--text-muted)' }}>$</span>
                <input type="number" step="0.01" value={addForm.amount} onChange={e => setAddForm(p => ({ ...p, amount: e.target.value }))}
                  autoFocus placeholder="0.00"
                  className="w-full rounded-lg pl-10 pr-4 py-4 text-2xl font-bold focus:outline-none"
                  style={{ color: 'var(--text-primary)', background: 'var(--bg-subtle)', border: '2px solid var(--border)' }}
                  onFocus={e => { e.currentTarget.style.borderColor = 'var(--accent-primary)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(109, 40, 217, 0.1)'; }}
                  onBlur={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; }}
                />
              </div>
            </div>

            {/* Vendor */}
            <div className="mb-4">
              <label className="block mb-1.5" style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)' }}>Vendor</label>
              {addingVendorInline ? (
                <div className="flex gap-2">
                  <input value={inlineVendorName} onChange={e => setInlineVendorName(e.target.value)} placeholder="Vendor name" autoFocus
                    onKeyDown={e => { if (e.key === 'Enter') addInlineVendor(); }}
                    className="flex-1 rounded-lg px-3 py-2 text-sm focus:outline-none"
                    style={{ border: '1px solid var(--border)', background: 'var(--bg-subtle)', color: 'var(--text-primary)' }}
                    onFocus={e => { e.currentTarget.style.borderColor = 'var(--accent-primary)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(109, 40, 217, 0.1)'; }}
                    onBlur={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; }}
                  />
                  <PrimaryBtn onClick={addInlineVendor} className="!px-3 !py-2">Add</PrimaryBtn>
                  <button onClick={() => setAddingVendorInline(false)} className="text-sm px-2" style={{ color: 'var(--text-secondary)', background: 'transparent', border: 'none', cursor: 'pointer' }}>Cancel</button>
                </div>
              ) : (
                <select value={addForm.vendor_id} onChange={e => { if (e.target.value === 'new') { setAddingVendorInline(true); } else setAddForm(p => ({ ...p, vendor_id: e.target.value })); }}
                  className="w-full rounded-lg px-3 py-2.5 text-sm focus:outline-none"
                  style={{ border: '1px solid var(--border)', background: 'var(--bg-subtle)', color: 'var(--text-primary)' }}
                >
                  <option value="">Quick entry (no vendor)</option>
                  {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                  <option value="new">+ Add new vendor</option>
                </select>
              )}
              {lastInvoiceForVendor && (
                <div className="text-xs mt-1.5" style={{ color: 'var(--text-muted)' }}>Last: {fmt$(lastInvoiceForVendor.total)} on {new Date(lastInvoiceForVendor.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
              )}
            </div>

            {/* Date */}
            <div className="mb-4">
              <label className="block mb-1.5" style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)' }}>Date</label>
              <input type="date" value={addForm.date} onChange={e => setAddForm(p => ({ ...p, date: e.target.value }))}
                className="w-full rounded-lg px-3 py-2.5 text-sm focus:outline-none"
                style={{ border: '1px solid var(--border)', background: 'var(--bg-subtle)', color: 'var(--text-primary)' }}
                onFocus={e => { e.currentTarget.style.borderColor = 'var(--accent-primary)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(109, 40, 217, 0.1)'; }}
                onBlur={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; }}
              />
            </div>

            {/* Optional details */}
            {!addForm.showDetails ? (
              <button
                onClick={() => setAddForm(p => ({ ...p, showDetails: true }))}
                className="text-xs mb-4 block"
                style={{ color: 'var(--accent-primary)', background: 'transparent', border: 'none', cursor: 'pointer', fontWeight: 600 }}
              >+ Add details (invoice #, notes)</button>
            ) : (
              <div className="grid grid-cols-2 gap-3 mb-4">
                {[
                  { key: 'invoice_number', label: 'Invoice #' },
                  { key: 'notes',          label: 'Notes' },
                ].map(f => (
                  <div key={f.key}>
                    <label className="block mb-1" style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)' }}>{f.label}</label>
                    <input
                      value={addForm[f.key]} onChange={e => setAddForm(p => ({ ...p, [f.key]: e.target.value }))}
                      placeholder="Optional"
                      className="w-full rounded-lg px-3 py-2 text-sm focus:outline-none"
                      style={{ border: '1px solid var(--border)', background: 'var(--bg-subtle)', color: 'var(--text-primary)' }}
                      onFocus={e => { e.currentTarget.style.borderColor = 'var(--accent-primary)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(109, 40, 217, 0.1)'; }}
                      onBlur={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; }}
                    />
                  </div>
                ))}
              </div>
            )}

            <PrimaryBtn onClick={saveAddCost} disabled={!addForm.amount || parseFloat(addForm.amount) <= 0} className="w-full !py-3">
              Save
            </PrimaryBtn>
          </div>
        </div>
      )}

    </div>
  );
}
